import { useReducer, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { Rocket, XIcon, Layers, Video, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { wizardReducer, initialState } from './wizard/reducer'
import { WizardStep1 } from './wizard/WizardStep1'
import { WizardStep2 } from './wizard/WizardStep2'
import { WizardStep3 } from './wizard/WizardStep3'
import { toast } from 'sonner'
import type { Account } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  accounts: Account[]
}

const STEPS = [
  { num: 1, label: 'Videos & Destinos' },
  { num: 2, label: 'Legendas' },
  { num: 3, label: 'Agendar & Publicar' },
] as const

export function CreatePostWizard({ open, onOpenChange, accounts }: Props) {
  const [state, dispatch] = useReducer(wizardReducer, initialState)

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false) }
    document.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey) }
  }, [open, onOpenChange])

  // Derived: selected destination count
  const selectedCount = useMemo(() => {
    let c = 0
    for (const v of Object.values(state.selectedAccounts)) { if (v.ig) c++; if (v.fb) c++ }
    for (const v of Object.values(state.selectedYtChannels)) { if (v) c++ }
    return c
  }, [state.selectedAccounts, state.selectedYtChannels])

  // Derived: available profiles from selected accounts
  const availableProfiles = useMemo(() => {
    const names = new Set<string>()
    for (const igUserId of Object.keys(state.selectedAccounts)) {
      const sel = state.selectedAccounts[igUserId]
      if (!sel.ig && !sel.fb) continue
      const acc = accounts.find(a => a.igUserId === igUserId)
      acc?.tokens?.forEach(t => { if (t.valid) names.add(t.profileName) })
    }
    return Array.from(names).sort()
  }, [state.selectedAccounts, accounts])

  const canAdvance = () => {
    if (state.step === 1) {
      if (state.items.length === 0) { toast.error('Adicione ao menos um video'); return false }
      if (selectedCount === 0) { toast.error('Selecione ao menos um destino'); return false }
      return true
    }
    if (state.step === 2) {
      const hasIG = Object.values(state.selectedAccounts).some(v => v.ig)
      const hasFB = Object.values(state.selectedAccounts).some(v => v.fb)
      const hasYT = Object.values(state.selectedYtChannels).some(v => v)
      if ((hasIG || hasFB) && !state.globalCaption.trim()) {
        const allOverridden = state.items.every(i => state.overrides[i.id]?.caption?.trim())
        if (!allOverridden) { toast.error('Preencha a legenda global ou customize cada video'); return false }
      }
      if (hasYT && !state.globalYtTitle.trim()) {
        const allOverridden = state.items.every(i => state.overrides[i.id]?.ytTitle?.trim())
        if (!allOverridden) { toast.error('Preencha o titulo YouTube global ou customize cada video'); return false }
      }
      return true
    }
    return true
  }

  const handleNext = () => {
    if (!canAdvance()) return
    if (state.step < 3) dispatch({ type: 'SET_STEP', step: (state.step + 1) as 1 | 2 | 3 })
  }

  const handleBack = () => {
    if (state.step > 1) dispatch({ type: 'SET_STEP', step: (state.step - 1) as 1 | 2 | 3 })
  }

  const handleClose = () => {
    dispatch({ type: 'RESET' })
    onOpenChange(false)
  }

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="absolute inset-4 flex flex-col overflow-hidden rounded-xl border border-[rgba(255,255,255,0.09)] bg-[#1c1c20]">

        {/* HEADER */}
        <div className="flex shrink-0 items-center gap-3 border-b border-[rgba(255,255,255,0.09)] px-5 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#c9a84c] to-[#9a7b2f]">
            <Rocket className="h-4 w-4 text-[#151518]" />
          </div>
          <span className="text-lg font-bold tracking-tight">Programar Videos Longos</span>

          {/* Feed/Story toggle */}
          <div className="flex gap-1 ml-2">
            <button type="button" onClick={() => dispatch({ type: 'SET_FIELD', field: 'postType', value: 'feed' })}
              className={cn('rounded-lg border px-3 py-1 text-xs font-medium transition-colors',
                state.postType === 'feed'
                  ? 'border-[#c9a84c]/30 bg-[#c9a84c]/10 text-[#c9a84c]'
                  : 'border-[rgba(255,255,255,0.09)] text-[#888580] hover:text-[#d0cdc7]')}>
              <Layers className="inline h-3.5 w-3.5 mr-1" />Feed
            </button>
            <button type="button" onClick={() => dispatch({ type: 'SET_FIELD', field: 'postType', value: 'story' })}
              className={cn('rounded-lg border px-3 py-1 text-xs font-medium transition-colors',
                state.postType === 'story'
                  ? 'border-[#d29922]/30 bg-[#d29922]/10 text-[#d29922]'
                  : 'border-[rgba(255,255,255,0.09)] text-[#888580] hover:text-[#d0cdc7]')}>
              <Video className="inline h-3.5 w-3.5 mr-1" />Story
            </button>
          </div>

          {/* Internal title */}
          <Input
            value={state.postTitle}
            onChange={e => dispatch({ type: 'SET_FIELD', field: 'postTitle', value: e.target.value })}
            placeholder="Titulo interno (opcional)"
            className="ml-3 h-8 max-w-[200px] text-xs border-dashed border-[rgba(255,255,255,0.09)] bg-transparent"
            maxLength={255}
          />

          {/* Preferred profile */}
          {availableProfiles.length > 0 && (
            <Select
              value={state.preferredProfile || '__auto__'}
              onValueChange={v => dispatch({ type: 'SET_FIELD', field: 'preferredProfile', value: v === '__auto__' ? '' : v })}
            >
              <SelectTrigger className="h-8 w-[160px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Perfil automatico</SelectItem>
                {availableProfiles.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          <div className="flex-1" />
          <button type="button" onClick={handleClose} className="rounded-lg border border-[rgba(255,255,255,0.09)] p-1.5 text-[#888580] hover:text-[#f5f5f3] transition-colors">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* STEPS BAR */}
        <div className="flex shrink-0 items-center gap-0 border-b border-[rgba(255,255,255,0.09)] bg-black/15 px-5 py-3">
          {STEPS.map((s, i) => (
            <div key={s.num} className="contents">
              <div className="flex items-center gap-2">
                <div className={cn('flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all',
                  state.step === s.num
                    ? 'bg-[#c9a84c] text-[#151518] shadow-[0_0_12px_rgba(201,168,76,0.4)]'
                    : state.step > s.num
                      ? 'bg-[#9a7b2f] text-[#151518]'
                      : 'border-[1.5px] border-[rgba(255,255,255,0.09)] text-[#888580]')}>
                  {state.step > s.num ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : s.num}
                </div>
                <span className={cn('text-xs transition-colors',
                  state.step === s.num ? 'text-[#c9a84c] font-semibold' :
                  state.step > s.num ? 'text-[#d0cdc7]' : 'text-[#888580]')}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={cn('mx-4 h-[1.5px] flex-1 max-w-[60px] transition-colors',
                  state.step > s.num ? 'bg-[#9a7b2f]' : 'bg-[rgba(255,255,255,0.09)]')} />
              )}
            </div>
          ))}
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto">
          {state.step === 1 && <WizardStep1 state={state} dispatch={dispatch} accounts={accounts} />}
          {state.step === 2 && <WizardStep2 state={state} dispatch={dispatch} accounts={accounts} />}
          {state.step === 3 && <WizardStep3 state={state} dispatch={dispatch} accounts={accounts} onClose={handleClose} />}
        </div>

        {/* FOOTER */}
        {state.step < 3 && (
          <div className="flex shrink-0 items-center justify-between border-t border-[rgba(255,255,255,0.09)] bg-black/15 px-5 py-3">
            {state.step > 1 ? (
              <Button variant="outline" size="sm" onClick={handleBack} className="gap-1 border-[rgba(255,255,255,0.09)] text-[#888580] hover:text-[#f5f5f3]">
                Voltar
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={handleClose} className="border-[rgba(255,255,255,0.09)] text-[#888580] hover:text-[#f5f5f3]">
                Cancelar
              </Button>
            )}
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#888580]">
                {state.step === 1 && `${state.items.length} video(s) · ${selectedCount} destino(s)`}
                {state.step === 2 && (() => {
                  const done = state.items.filter(i => state.overrides[i.id] || state.globalCaption.trim()).length
                  return <span className={done === state.items.length ? 'text-[#22c55e]' : ''}>{done}/{state.items.length} legendados</span>
                })()}
              </span>
              <Button size="sm" onClick={handleNext}
                className="gap-1 bg-gradient-to-r from-[#c9a84c] to-[#9a7b2f] text-[#151518] font-semibold hover:shadow-[0_0_16px_rgba(201,168,76,0.3)]">
                Proximo
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
